import { post } from './fetch.ts';

/**
 * Convert a base64url-encoded string to an ArrayBuffer.
 * The WebAuthn browser API requires ArrayBuffer for challenge and user.id fields.
 */
function base64urlToArrayBuffer(base64url: string): ArrayBuffer {
  let base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  while (base64.length % 4) {
    base64 += '=';
  }
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Convert an ArrayBuffer to a base64url-encoded string.
 * The WebAuthn API server expects base64url-encoded fields in the finalize request.
 */
function arrayBufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// Registration flow (authenticated user adding a passkey):
//   1. POST /v1/webauthn/begin-registration — server returns a challenge and credential creation options
//   2. Call prepareCreationOptions() to convert base64url fields to ArrayBuffers for the browser API
//   3. Call navigator.credentials.create() to prompt the user to create a credential
//   4. Call serializeCredential() to convert the credential back to base64url for the server
//   5. POST /v1/webauthn/finalize-registration — server verifies and stores the credential

// POST /v1/webauthn/begin-registration
type BeginRegistrationResult = {
  warsToken: string;
  credentialCreation: PublicKeyCredentialCreationOptionsJSON;
};

type PublicKeyCredentialCreationOptionsJSON = {
  publicKey: {
    challenge: string;
    rp: { name: string; id: string };
    user: { id: string; name: string; displayName: string };
    pubKeyCredParams: Array<{ type: string; alg: number }>;
    timeout?: number;
    attestation?: AttestationConveyancePreference;
    authenticatorSelection?: AuthenticatorSelectionCriteria;
  };
};

export function beginRegistration() {
  return post<BeginRegistrationResult>('/v1/webauthn/begin-registration');
}

/**
 * Prepare the credentialCreation options from the server for the browser's
 * navigator.credentials.create() API by converting base64url strings to ArrayBuffers.
 */
export function prepareCreationOptions(
  credentialCreation: PublicKeyCredentialCreationOptionsJSON
): CredentialCreationOptions {
  return {
    publicKey: {
      ...credentialCreation.publicKey,
      challenge: base64urlToArrayBuffer(credentialCreation.publicKey.challenge),
      user: {
        ...credentialCreation.publicKey.user,
        id: base64urlToArrayBuffer(credentialCreation.publicKey.user.id),
      },
      pubKeyCredParams: credentialCreation.publicKey.pubKeyCredParams as PublicKeyCredentialParameters[],
    },
  };
}

export function isPublicKeyCredential(c: Credential | null): c is PublicKeyCredential {
  return c != null && (c.type === 'public-key' || c instanceof PublicKeyCredential);
}

/**
 * Matches the WebAuthn cancellation/timeout rejection (NotAllowedError) from
 * navigator.credentials.get()/create(), which the user can trigger at any time by
 * dismissing the prompt. Password-manager extensions that shim WebAuthn can surface
 * it as a cross-realm DOMException that fails instanceof checks, or rethrow it as a
 * plain Error whose message leads with the original name — so match on shape, not
 * class (same reasoning as the type === 'public-key' branch in isPublicKeyCredential).
 */
export function isWebAuthnCancellation(err: unknown): boolean {
  if (err == null || typeof err !== 'object') {
    return false;
  }
  const { name, message } = err as { name?: unknown; message?: unknown };
  return name === 'NotAllowedError' || (typeof message === 'string' && message.startsWith('NotAllowedError:'));
}

/**
 * Matches the rejection (AbortError) from navigator.credentials.get()/create() when a
 * pending ceremony is cancelled via an AbortSignal, e.g. because the screen that started
 * it unmounted. Matched on shape rather than class for the same cross-realm reasons as
 * isWebAuthnCancellation.
 */
export function isWebAuthnAbort(err: unknown): boolean {
  if (err == null || typeof err !== 'object') {
    return false;
  }
  const { name, message } = err as { name?: unknown; message?: unknown };
  return name === 'AbortError' || (typeof message === 'string' && message.startsWith('AbortError:'));
}

/**
 * Serialize a PublicKeyCredential from navigator.credentials.create() into a
 * JSON-compatible object for the finalize-registration endpoint.
 */
export function serializeCredential(credential: PublicKeyCredential): Record<string, unknown> {
  const response = credential.response as AuthenticatorAttestationResponse;
  return {
    id: credential.id,
    rawId: arrayBufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: arrayBufferToBase64url(response.clientDataJSON),
      attestationObject: arrayBufferToBase64url(response.attestationObject),
      transports: response.getTransports(),
    },
  };
}

// POST /v1/webauthn/finalize-registration
type FinalizeRegistrationResult = {
  authToken: string;
};
type FinalizeRegistrationVariables = {
  warsToken: string;
  credentialCreationResponse: Record<string, unknown>;
};

export function finalizeRegistration(data: FinalizeRegistrationVariables) {
  return post<FinalizeRegistrationResult>('/v1/webauthn/finalize-registration', data);
}

// Authentication flow (unauthenticated user logging in with a passkey):
//   1. POST /v1/auth/webauthn/begin — server returns a challenge and assertion options
//   2. Call prepareAssertionOptions() to convert base64url fields to ArrayBuffers for the browser API
//   3. Call navigator.credentials.get() to prompt the user to select and use a credential
//   4. Call serializeAssertion() to convert the assertion back to base64url for the server
//   5. POST /v1/auth/webauthn/finalize — server verifies the assertion and returns session/auth tokens

// POST /v1/auth/webauthn/begin
type BeginAuthenticationResult = {
  walsToken: string;
  credentialAssertion: {
    publicKey: {
      challenge: string;
      timeout?: number;
      rpId?: string;
      userVerification?: UserVerificationRequirement;
      allowCredentials?: Array<{ type: string; id: string }>;
    };
  };
};

export function beginAuthentication() {
  return post<BeginAuthenticationResult>('/v1/auth/webauthn/begin');
}

/**
 * Prepare the credentialAssertion options from the server for the browser's
 * navigator.credentials.get() API by converting base64url strings to ArrayBuffers.
 */
export function prepareAssertionOptions(
  credentialAssertion: BeginAuthenticationResult['credentialAssertion']
): CredentialRequestOptions {
  return {
    publicKey: {
      ...credentialAssertion.publicKey,
      challenge: base64urlToArrayBuffer(credentialAssertion.publicKey.challenge),
      allowCredentials: credentialAssertion.publicKey.allowCredentials?.map((cred) => ({
        type: cred.type as PublicKeyCredentialType,
        id: base64urlToArrayBuffer(cred.id),
      })),
    },
  };
}

/**
 * Serialize a PublicKeyCredential from navigator.credentials.get() into a
 * JSON-compatible object for the finalize-authentication endpoint.
 */
export function serializeAssertion(credential: PublicKeyCredential): Record<string, unknown> {
  const response = credential.response as AuthenticatorAssertionResponse;
  return {
    id: credential.id,
    rawId: arrayBufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: arrayBufferToBase64url(response.clientDataJSON),
      authenticatorData: arrayBufferToBase64url(response.authenticatorData),
      signature: arrayBufferToBase64url(response.signature),
      userHandle: response.userHandle ? arrayBufferToBase64url(response.userHandle) : null,
    },
  };
}

// POST /v1/auth/webauthn/finalize
type FinalizeAuthenticationResult = {
  sessionToken: string;
  authToken: string;
};
type FinalizeAuthenticationVariables = {
  walsToken: string;
  credentialAssertionResponse: Record<string, unknown>;
};

export function finalizeAuthentication(data: FinalizeAuthenticationVariables) {
  return post<FinalizeAuthenticationResult>('/v1/auth/webauthn/finalize', data);
}
